import { useId, type ReactNode } from 'react';
import { EyeOff, Info, Loader2, Lock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NoteField from './recipients/NoteField';
import RecipientPicker from './recipients/RecipientPicker';
import SendOutcomeLine from './SendOutcomeLine';
import ShareTabHeader from './ShareTabHeader';
import { INTERNAL_SHARE_BLOCK_COPY, type InternalShare } from './useInternalShare';

interface InternalShareTabProps {
  share: InternalShare;
  /** The property's account, named in the no-match line. */
  accountName: string | null;
  /** Switch to the PlanOtour tab. */
  onUseGallery: () => void;
  onClose: () => void;
}

/**
 * The Share modal's Internal Share tab (DV-3715): emails the photoset to
 * members of the account's team, who sign in to see it. Nobody starts
 * checked; Send is withheld while the photoset is hidden from the client,
 * since the link would open on an unavailable page until the reveal.
 */
const InternalShareTab = ({ share, accountName, onUseGallery, onClose }: InternalShareTabProps) => {
  const blockId = useId();
  const { team, block } = share;
  const pickedCount = team.picked.length;

  const galleryLink = (
    <Button
      type="button"
      variant="link"
      onClick={onUseGallery}
      data-testid="share-gallery-inline-link"
      className="text-sm underline"
    >
      PlanOtour
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <ShareTabHeader
        icon={Lock}
        title="Send Photoset to your team"
        audience="Team members · Sign-in required"
        tone="muted"
      />
      <div className="flex flex-col gap-3">
        <div className="flex flex-col rounded-lg border border-border bg-background">
          <RecipientPicker
            team={team}
            disabled={share.sending}
            renderNoMatch={(query) => (
              <NoMatchLine query={query} accountName={accountName} galleryLink={galleryLink} />
            )}
            empty={
              <>
                No one on this team can receive it yet. Members appear once they’ve accepted their
                invite and verified their email. Use {galleryLink} to share with anyone else.
              </>
            }
          />
          <NoteField
            label="Note"
            value={share.note}
            onChange={share.setNote}
            disabled={share.sending}
          />
        </div>
        <SendOutcomeLine outcome={share.outcome} />
      </div>
      {block !== null && (
        <p id={blockId} className="m-0 flex items-start gap-2 text-sm text-muted-foreground">
          {block === 'hidden' ? (
            <EyeOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          )}
          <span>{INTERNAL_SHARE_BLOCK_COPY[block]}</span>
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={share.sending}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={share.send}
          disabled={!share.canSend}
          aria-describedby={block !== null ? blockId : undefined}
          className="w-full sm:w-auto"
        >
          {share.sending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="h-4 w-4" aria-hidden />
          )}
          {sendLabel(share.sending, pickedCount)}
        </Button>
      </div>
    </div>
  );
};

/** "Send", or "Send to N People" above one. */
const sendLabel = (sending: boolean, pickedCount: number): string => {
  if (sending) return 'Sending…';
  return pickedCount > 1 ? `Send to ${pickedCount} People` : 'Send';
};

interface NoMatchLineProps {
  query: string;
  accountName: string | null;
  galleryLink: ReactNode;
}

const NoMatchLine = ({ query, accountName, galleryLink }: NoMatchLineProps) => (
  <>
    No team members match “{query}”. Only {accountName ?? 'this account’s'} team members can
    receive this link; use {galleryLink} for anyone else.
  </>
);

export default InternalShareTab;
